import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, CircleAlert, Copy, Infinity as InfinityIcon, Plus, Ticket, UserPlus, Users } from 'lucide-react'
import { useState, type CSSProperties } from 'react'

import { createInviteCode, getInviteCodes, getPlatformConfig, revokeInviteCode, type InviteCode } from '../api/admin'
import { PageHeader, PageSection } from '../components/layout'
import { Badge, Button, EmptyState, Field, IconButton, Input, Modal, Progress, Skeleton, StatCard, cx, describeError, ui, useToast, when, type Tone } from '../components/ui/Ui'
import styles from './Invites.module.css'

/**
 * Registration takes a code (`auth.invitation_required`). A member's personal
 * code makes that member the referrer; an operator's invitation from this
 * screen lets a person in with no referrer — for a channel, a partner, a call.
 */

type InviteStatus = 'active' | 'exhausted' | 'expired' | 'revoked'

const STATUS_RU: Record<InviteStatus, string> = {
  active: 'активен',
  exhausted: 'исчерпан',
  expired: 'истёк',
  revoked: 'отозван',
}

const TONE: Record<InviteStatus, Tone> = {
  active: 'accent',
  exhausted: 'neutral',
  expired: 'neutral',
  revoked: 'down',
}

function statusOf(invite: InviteCode, now: number): InviteStatus {
  if (invite.revokedAt !== null) return 'revoked'
  if (invite.expiresAt !== null && new Date(invite.expiresAt).getTime() <= now) return 'expired'
  if (invite.maxUses !== null && invite.uses >= invite.maxUses) return 'exhausted'
  // The server's clock decides expiry; the only reason the row can miss is that one.
  return invite.isActive ? 'active' : 'expired'
}

/** Local datetime input value ↔ ISO; empty stays empty. */
function toLocal(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
function fromLocal(value: string): string | null {
  return value === '' ? null : new Date(value).toISOString()
}

/** What is left until a moment, in the largest whole unit: «через 3 дн», «через 5 ч», «через 12 мин». */
function inWords(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000))
  if (minutes < 60) return `через ${minutes} мин`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `через ${hours} ч`
  return `через ${Math.round(hours / 24)} дн`
}

/** Russian plural: 1 код, 2 кода, 5 кодов. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

interface Draft {
  code: string
  label: string
  maxUses: string
  expiresAt: string | null
}

const EMPTY: Draft = { code: '', label: '', maxUses: '', expiresAt: null }

export default function Invites(): JSX.Element {
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [revoking, setRevoking] = useState<InviteCode | null>(null)
  const queryClient = useQueryClient()
  const toast = useToast()
  const list = useQuery({ queryKey: ['invite-codes'], queryFn: getInviteCodes, refetchInterval: 30_000 })
  // Public config: every desk may read whether a code is required, `settings` or not.
  const platform = useQuery({ queryKey: ['platform-config'], queryFn: getPlatformConfig, staleTime: 60_000 })
  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['invite-codes'] })

  const create = useMutation({
    mutationFn: () =>
      createInviteCode({
        ...(draft.code.trim() === '' ? {} : { code: draft.code.trim().toUpperCase() }),
        label: draft.label.trim(),
        maxUses: draft.maxUses.trim() === '' ? null : Number(draft.maxUses),
        expiresAt: draft.expiresAt,
      }),
    onSuccess: (invite) => {
      toast(`Код ${invite.code} создан`)
      setCreating(false)
      setDraft(EMPTY)
      refresh()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => revokeInviteCode(id),
    onSuccess: (invite) => {
      toast(`Код ${invite.code} отозван`)
      setRevoking(null)
      refresh()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const copy = (code: string): void => {
    // No clipboard API outside https/localhost: say so instead of failing silently.
    if (typeof navigator.clipboard?.writeText !== 'function') {
      toast('Браузер не дал скопировать — выделите код вручную', true)
      return
    }
    navigator.clipboard
      .writeText(code)
      .then(() => toast(`${code} скопирован`))
      .catch(() => toast('Браузер не дал скопировать — выделите код вручную', true))
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => setDraft((current) => ({ ...current, [key]: value }))
  const codeText = draft.code.trim().toUpperCase()
  const codeError = codeText !== '' && !/^[A-Z0-9_-]{4,32}$/.test(codeText) ? 'Латиница, цифры, «-» и «_», от 4 до 32 символов.' : null
  const maxUsesError = draft.maxUses.trim() !== '' && !(Number.isInteger(Number(draft.maxUses)) && Number(draft.maxUses) >= 1 && Number(draft.maxUses) <= 1_000_000) ? 'Целое число от 1 до 1 000 000 или пусто.' : null
  const expiresError = draft.expiresAt !== null && new Date(draft.expiresAt).getTime() <= Date.now() ? 'Срок уже прошёл.' : null
  const valid = draft.label.trim().length > 0 && draft.label.trim().length <= 200 && codeError === null && maxUsesError === null && expiresError === null

  const now = Date.now()
  const rows = list.data?.items ?? []
  const withStatus = rows.map((row) => ({ row, status: statusOf(row, now) }))
  const active = withStatus.filter((item) => item.status === 'active')
  const inactive = withStatus.filter((item) => item.status !== 'active')
  const registrations = rows.reduce((sum, row) => sum + row.uses, 0)
  const unlimited = active.filter(({ row }) => row.maxUses === null).length
  const seatsLeft = active.reduce((sum, { row }) => sum + (row.maxUses === null ? 0 : Math.max(0, row.maxUses - row.uses)), 0)
  const required = platform.data?.invitationRequired
  const openCreate = (): void => {
    setDraft(EMPTY)
    setCreating(true)
  }

  return (
    <>
      <PageHeader
        title="Коды приглашений"
        description={
          required === false
            ? 'Сейчас код при регистрации необязателен; коды отсюда всё равно работают. Подходит и личный код любого участника (он станет пригласившим).'
            : 'Регистрация сейчас только по коду. Подходит личный код любого участника (он станет пригласившим) или код приглашения отсюда (без пригласившего).'
        }
        meta={
          platform.data ? (
            <>
              <Badge tone={required === false ? 'warning' : 'accent'} size="sm">
                {required === false ? 'код необязателен' : 'регистрация только по коду'}
              </Badge>
              <span className={styles.settingKey} title="Настройка, которая включает регистрацию только по коду">
                auth.invitation_required {required === false ? 'выключена' : 'включена'}
              </span>
            </>
          ) : (
            <Skeleton width={180} height={20} radius={999} />
          )
        }
        actions={
          <Button variant="primary" icon={<Plus />} onClick={openCreate}>
            Создать код
          </Button>
        }
      />

      <div className={styles.kpis}>
        <StatCard label="Активных кодов" value={list.data ? active.length : null} loading={list.isPending} icon={<Ticket />} hint={list.data ? `из ${rows.length} ${plural(rows.length, 'созданного', 'созданных', 'созданных')}` : undefined} />
        <StatCard label="Регистраций по кодам" value={list.data ? registrations : null} loading={list.isPending} icon={<UserPlus />} tone="info" hint="по всем кодам отсюда, без пригласившего" />
        <StatCard
          label="Свободных мест"
          value={list.data ? (active.length > 0 && unlimited === active.length ? '∞' : seatsLeft) : null}
          loading={list.isPending}
          icon={<Users />}
          tone="neutral"
          hint={unlimited > 0 ? `и ${unlimited} ${plural(unlimited, 'код', 'кода', 'кодов')} без лимита` : 'в активных кодах с лимитом'}
        />
      </div>

      {list.isError ? (
        <div className={styles.errorPanel}>
          <EmptyState tone="error" icon={<CircleAlert />} title="Коды не загрузились" description={describeError(list.error)} />
        </div>
      ) : list.isPending ? (
        <div className={styles.grid} aria-busy="true">
          {[0, 1, 2].map((key) => (
            <div key={key} className={styles.card}>
              <Skeleton width={120} height={22} />
              <Skeleton width="70%" height={14} />
              <Skeleton height={6} radius={999} />
              <Skeleton width="50%" height={12} />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.errorPanel}>
          <EmptyState
            icon={<Ticket />}
            title="Кодов приглашения нет"
            description="Создайте первый кнопкой «Создать код»; личные коды участников при регистрации тоже подходят."
            action={
              <Button variant="primary" icon={<Plus />} onClick={openCreate}>
                Создать код
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <PageSection title="Действующие" description={active.length === 0 ? 'Ни один код сейчас не принимается при регистрации.' : 'Принимаются при регистрации прямо сейчас.'} actions={<span className={styles.count}>{active.length}</span>}>
            {active.length === 0 ? (
              <div className={styles.errorPanel}>
                <EmptyState compact icon={<Ticket />} title="Действующих кодов нет" description="Создайте новый кнопкой «Создать код»." />
              </div>
            ) : (
              <div className={cx(styles.grid, 'stagger')}>
                {active.map(({ row, status }) => (
                  <InviteCard key={row.id} row={row} status={status} now={now} onCopy={copy} onRevoke={() => setRevoking(row)} />
                ))}
              </div>
            )}
          </PageSection>
          {inactive.length > 0 ? (
            <PageSection title="Больше не действуют" description="Исчерпанные, истёкшие и отозванные коды: при регистрации не принимаются." actions={<span className={styles.count}>{inactive.length}</span>}>
              <div className={cx(styles.grid, 'stagger')}>
                {inactive.map(({ row, status }) => (
                  <InviteCard key={row.id} row={row} status={status} now={now} onCopy={copy} />
                ))}
              </div>
            </PageSection>
          ) : null}
        </>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Новый код приглашения"
        icon={<Ticket />}
        subtitle="Человек вводит код при регистрации. Пригласившего у него не будет — партнёрская программа за него никому не начисляет."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Отмена
            </Button>
            <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={() => create.mutate()}>
              Создать
            </Button>
          </>
        }
      >
        <div className={ui.stack}>
          <Field label="Метка" hint="Для себя: кому и зачем выдан — «Канал Иванова», «Созвон 15 октября»." aside={`${draft.label.trim().length} / 200`}>
            <Input value={draft.label} maxLength={200} onChange={(event) => set('label', event.target.value)} />
          </Field>
          <div className={ui.grid3}>
            <Field label="Код" hint="Пусто — система придумает 8 символов." error={codeError}>
              <Input mono value={draft.code} maxLength={32} placeholder="авто" onChange={(event) => set('code', event.target.value.toUpperCase())} />
            </Field>
            <Field label="Лимит" hint="Регистраций по коду. Пусто — без лимита." error={maxUsesError}>
              <Input mono type="number" min={1} value={draft.maxUses} placeholder="∞" onChange={(event) => set('maxUses', event.target.value)} />
            </Field>
            <Field label="Действует до" hint="Пусто — бессрочно." error={expiresError}>
              <Input type="datetime-local" value={toLocal(draft.expiresAt)} onChange={(event) => set('expiresAt', fromLocal(event.target.value))} />
            </Field>
          </div>
          <p className={styles.modalNote}>Код не должен совпадать с личным кодом участника или с другим кодом приглашения — сервер такой не примет.</p>
        </div>
      </Modal>

      <Modal
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title="Отозвать код?"
        icon={<Ban />}
        tone="down"
        size="sm"
        subtitle={revoking === null ? undefined : `${revoking.code} · ${revoking.label}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRevoking(null)}>
              Отмена
            </Button>
            <Button variant="danger-solid" loading={revoke.isPending} onClick={() => revoking && revoke.mutate(revoking.id)}>
              Отозвать
            </Button>
          </>
        }
      >
        <p className={styles.modalText}>
          Код перестанет приниматься при регистрации сразу. Уже зарегистрированные по нему остаются как есть. Вернуть код нельзя — при необходимости создайте новый.
        </p>
      </Modal>
    </>
  )
}

/**
 * One code as a card: the code with a copy button, its status, what it is
 * for, how much of its limit is used, until when it works, who made it.
 */
function InviteCard({ row, status, now, onCopy, onRevoke }: { row: InviteCode; status: InviteStatus; now: number; onCopy: (code: string) => void; onRevoke?: () => void }): JSX.Element {
  const limited = row.maxUses !== null
  const share = limited && row.maxUses ? row.uses / row.maxUses : 0
  const barTone: Tone = status !== 'active' ? 'neutral' : share >= 0.9 ? 'down' : share >= 0.75 ? 'warning' : 'accent'
  const expiresIn = row.expiresAt === null ? null : Date.parse(row.expiresAt) - now
  const soon = status === 'active' && expiresIn !== null && expiresIn > 0 && expiresIn < 3 * 86_400_000
  return (
    <article className={cx(styles.card, status !== 'active' ? styles.cardInactive : '')} style={{ '--share': Math.min(1, share) } as CSSProperties}>
      <header className={styles.cardHead}>
        <span className={styles.code}>
          <span className={styles.codeText}>{row.code}</span>
          <IconButton size="sm" icon={<Copy />} label={`Скопировать ${row.code}`} onClick={() => onCopy(row.code)} className={styles.copy} />
        </span>
        <Badge tone={TONE[status]} size="sm">
          {STATUS_RU[status]}
        </Badge>
      </header>
      <p className={styles.label} title={row.label}>
        {row.label}
      </p>

      <div className={styles.usage}>
        <div className={styles.usageHead}>
          <span className={styles.usageLabel}>Использовано</span>
          <span className={styles.usageFigure}>
            <span className="num">{row.uses}</span>
            <span className={styles.usageOf}>
              {' / '}
              {limited ? <span className="num">{row.maxUses}</span> : <InfinityIcon aria-label="без лимита" className={styles.infinity} />}
            </span>
          </span>
        </div>
        {limited ? (
          <Progress value={row.uses} max={row.maxUses ?? 1} tone={barTone} label={`Использовано ${row.uses} из ${row.maxUses ?? 0}`} />
        ) : (
          <span className={styles.unlimitedBar} aria-hidden="true" />
        )}
        <div className={styles.usageFoot}>
          {limited ? (
            status === 'active' ? (
              <>
                осталось <span className="num">{Math.max(0, (row.maxUses ?? 0) - row.uses)}</span>
              </>
            ) : (
              <span className="num">{Math.round(share * 100)} %</span>
            )
          ) : (
            'без лимита'
          )}
        </div>
      </div>

      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Действует до</dt>
          <dd>
            {row.expiresAt === null ? 'бессрочно' : <span className="num">{when(row.expiresAt)}</span>}
            {soon && expiresIn !== null ? <span className={styles.soon}>{inWords(expiresIn)}</span> : null}
          </dd>
        </div>
        {row.revokedAt !== null ? (
          <div className={styles.fact}>
            <dt>Отозван</dt>
            <dd className="num">{when(row.revokedAt)}</dd>
          </div>
        ) : null}
        <div className={styles.fact}>
          <dt>Создал</dt>
          <dd className={styles.truncate} title={row.createdBy?.email ?? undefined}>
            {row.createdBy?.email ?? '—'}
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>Создан</dt>
          <dd className="num">{when(row.createdAt)}</dd>
        </div>
      </dl>

      {onRevoke ? (
        <footer className={styles.cardFoot}>
          <Button size="sm" variant="danger" icon={<Ban />} onClick={onRevoke}>
            Отозвать
          </Button>
        </footer>
      ) : null}
    </article>
  )
}
